import { WEBHOOK_DELIVERY_STATUS_LABELS, type WebhookDeliveryStatus } from '@lia/contracts';
import * as React from 'react';
import { EMPTY, formatDateTime, formatRelative } from '../../lib/format.js';
import { DataTable, type DataTableColumn } from '../layout/data-table.js';
import { StatusBadge } from '../primitives/badge.js';
import { EmptyState } from '../primitives/states.js';
import { WEBHOOK_DELIVERY_STATUS_TONE } from './status.js';

export interface WebhookDeliveryRow {
  id: string;
  event: string;
  status: WebhookDeliveryStatus;
  /** null = no response received. */
  httpStatus: number | null;
  durationMs: number | null;
  attempts: number;
  maxAttempts?: number;
  nextRetryAt: string | null;
  createdAt: string;
}

export interface WebhookLogProps {
  deliveries: readonly WebhookDeliveryRow[];
  timezone: string;
  loading?: boolean;
  /** Error / permission state replacing the body. */
  error?: React.ReactNode;
  onRowClick?: (row: WebhookDeliveryRow) => void;
  footer?: React.ReactNode;
  now?: Date;
  className?: string;
}

/** Outgoing webhook delivery log. */
export function WebhookLog({ deliveries, timezone, loading, error, onRowClick, footer, now, className }: WebhookLogProps) {
  const columns: DataTableColumn<WebhookDeliveryRow>[] = [
    { id: 'event', header: 'Evento', cell: (r) => <span className="font-mono text-meta">{r.event}</span> },
    { id: 'status', header: 'Estado', cell: (r) => <StatusBadge tone={WEBHOOK_DELIVERY_STATUS_TONE[r.status]}>{WEBHOOK_DELIVERY_STATUS_LABELS[r.status]}</StatusBadge> },
    { id: 'http', header: 'HTTP', numeric: true, cell: (r) => (r.httpStatus === null ? <span className="text-text-secondary">Sin respuesta</span> : r.httpStatus) },
    { id: 'duration', header: 'Duración', numeric: true, hideOnMobile: true, cell: (r) => (r.durationMs === null ? EMPTY : `${r.durationMs} ms`) },
    { id: 'attempts', header: 'Intentos', numeric: true, cell: (r) => (r.maxAttempts ? `${r.attempts} de ${r.maxAttempts}` : r.attempts) },
    {
      id: 'next',
      header: 'Siguiente intento',
      hideOnMobile: true,
      cell: (r) => (r.nextRetryAt ? <span title={formatDateTime(r.nextRetryAt, timezone)}>{formatRelative(r.nextRetryAt, now)}</span> : <span className="text-text-secondary">{EMPTY}</span>),
    },
    { id: 'created', header: 'Creado', hideOnMobile: true, cell: (r) => <span className="ef-tabular">{formatDateTime(r.createdAt, timezone)}</span> },
  ];
  return (
    <DataTable
      className={className}
      caption="Entregas de webhooks"
      columns={columns}
      rows={deliveries}
      getRowId={(r) => r.id}
      loading={loading}
      error={error}
      onRowClick={onRowClick}
      rowActionLabel={(r) => `Ver entrega ${r.event}`}
      empty={<EmptyState compact title="Sin entregas todavía" description="Las entregas aparecerán aquí cuando ocurra un evento suscrito." />}
      footer={footer}
    />
  );
}
